/**
 * NotificationPanel — suite-standard two-tab notification panel.
 *
 * Wired into beheardShellAdapter.adapters.NotificationPanel.
 * The shell owns the container, open/close behavior, and positioning.
 * This component owns the content.
 *
 * Tabs:
 *   - "This App"   — notifications scoped to BeHeard (appId === 'beheard')
 *   - "Workspace"  — workspace-level notifications (appId === null)
 *
 * Data source: NotificationProvider (central-notifications SSE).
 * Mark-read goes through the provider's `markAsRead` (PATCH to Central
 * with optimistic update + revert on failure).
 */

import { useMemo, useState } from 'react';
import clsx from 'clsx';
import type { NotificationPanelProps } from '@levelup/ui-shell';
import {
  useNotifications,
  type NotificationListItem,
  type NotificationTab,
} from '../../context/NotificationProvider';

export function NotificationPanel({ isOpen, onClose }: NotificationPanelProps) {
  const [activeTab, setActiveTab] = useState<NotificationTab>('app');
  const { notifications, unreadCount, isInitialLoading, lastError, markAsRead, markAllAsRead } =
    useNotifications();

  const filtered = useMemo(
    () => notifications.filter((n) => n.tab === activeTab),
    [notifications, activeTab],
  );

  const tabUnread = useMemo(() => {
    const counts: Record<NotificationTab, number> = { app: 0, workspace: 0 };
    for (const n of notifications) if (!n.isRead) counts[n.tab] += 1;
    return counts;
  }, [notifications]);

  if (!isOpen) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex items-end border-b border-gray-200 px-4 pt-1 gap-1 flex-shrink-0">
        <TabButton
          label="This App"
          active={activeTab === 'app'}
          unreadCount={tabUnread.app}
          onClick={() => setActiveTab('app')}
        />
        <TabButton
          label="Workspace"
          active={activeTab === 'workspace'}
          unreadCount={tabUnread.workspace}
          onClick={() => setActiveTab('workspace')}
        />
        <div className="ml-auto pb-1">
          {unreadCount > 0 && (
            <button
              onClick={() => markAllAsRead()}
              className="text-xs text-primary-600 hover:underline"
            >
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {isInitialLoading && filtered.length === 0 ? (
          <LoadingState />
        ) : lastError && filtered.length === 0 ? (
          <ErrorState message={lastError} />
        ) : filtered.length === 0 ? (
          <EmptyState tab={activeTab} onClose={onClose} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((n) => (
              <NotificationRow key={n.id} item={n} onMarkRead={markAsRead} onAction={onClose} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Tab button
// ──────────────────────────────────────────────────────────────

function TabButton({
  label,
  active,
  unreadCount,
  onClick,
}: {
  label: string;
  active: boolean;
  unreadCount: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5',
        active
          ? 'border-primary-600 text-primary-700'
          : 'border-transparent text-gray-500 hover:text-gray-700',
      )}
    >
      {label}
      {unreadCount > 0 && (
        <span
          className={clsx(
            'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full',
            active ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700',
          )}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────
// Notification row
// ──────────────────────────────────────────────────────────────

function NotificationRow({
  item,
  onMarkRead,
  onAction,
}: {
  item: NotificationListItem;
  onMarkRead: (id: string) => void;
  onAction: () => void;
}) {
  const handleClick = () => {
    if (!item.isRead) onMarkRead(item.id);
    if (item.raw.actionUrl) {
      // Internal links: navigate; external: new tab. Match panel-close semantics.
      const url = item.raw.actionUrl;
      if (/^https?:\/\//.test(url)) {
        window.open(url, '_blank', 'noopener');
      } else {
        window.location.href = url;
      }
      onAction();
    }
  };

  return (
    <li>
      <button
        onClick={handleClick}
        className={clsx(
          'w-full text-left px-4 py-3 flex gap-3 hover:bg-gray-50 transition-colors',
          !item.isRead && 'bg-blue-50/40',
        )}
      >
        <span
          className={clsx(
            'mt-1 w-2 h-2 rounded-full flex-shrink-0',
            item.isRead ? 'bg-transparent' : 'bg-primary-500',
          )}
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
            <time className="text-[11px] text-gray-500 flex-shrink-0">
              {formatRelativeTime(item.createdAt)}
            </time>
          </div>
          {item.body && (
            <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{item.body}</p>
          )}
          {item.raw.actionLabel && (
            <span className="inline-block mt-1.5 text-xs text-primary-700 font-medium">
              {item.raw.actionLabel} →
            </span>
          )}
        </div>
      </button>
    </li>
  );
}

// ──────────────────────────────────────────────────────────────
// State views
// ──────────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-3">
      <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
      <p className="text-xs text-gray-500">Loading notifications…</p>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-3">
      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
        <svg className="w-6 h-6 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900">Couldn't load notifications</p>
        <p className="text-xs text-gray-500 mt-1">{message}</p>
      </div>
    </div>
  );
}

function EmptyState({ tab, onClose }: { tab: NotificationTab; onClose: () => void }) {
  if (tab === 'app') {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
          <BellIcon className="w-6 h-6 text-gray-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">No notifications yet</p>
          <p className="text-xs text-gray-500 mt-1">
            Practice session results, coaching updates, and achievements will appear here.
          </p>
        </div>
        <button onClick={onClose} className="text-xs text-primary-600 hover:underline mt-1">
          Manage notification settings →
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-3">
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
        <BuildingIcon className="w-6 h-6 text-gray-400" />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900">No workspace notifications</p>
        <p className="text-xs text-gray-500 mt-1">
          Member changes, billing events, and workspace updates will appear here.
        </p>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Formatting helpers
// ──────────────────────────────────────────────────────────────

function formatRelativeTime(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const now = Date.now();
  const diff = Math.max(0, now - ts);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ts).toLocaleDateString();
}

// ──────────────────────────────────────────────────────────────
// Icons
// ──────────────────────────────────────────────────────────────

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}
